<template>
  <div class="rcs-step rcs-step--test-numbers">
    <h2 class="rcs-step__heading">
      {{ t('storeParameters.services.rcsServiceParameter.wizard.testNumbers.title') }}
    </h2>
    <p class="rcs-step__lead">
      {{ t('storeParameters.services.rcsServiceParameter.wizard.testNumbers.subtitle') }}
    </p>

    <div class="rcs-carrier-repeater">
      <div
        v-for="(entry, entryIndex) in testNumbers.numbers"
        :key="`test-number-${entryIndex}`"
        class="rcs-carrier-repeater__card"
      >
        <div class="rcs-carrier-repeater__card-header">
          <p class="rcs-carrier-repeater__card-title">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.testNumbers.labels.number', { n: entryIndex + 1 }) }}
          </p>
          <button
            v-if="testNumbers.numbers.length"
            type="button"
            class="rcs-carrier-repeater__remove"
            @click="removeNumber(entryIndex)"
          >
            <i class="fa-solid fa-trash-can" aria-hidden="true" />
            {{ t('storeParameters.services.rcsServiceParameter.wizard.testNumbers.actions.remove') }}
          </button>
        </div>

        <div class="rcs-form-grid">
          <div class="rcs-form-grid__field rcs-form-grid__field--full">
            <label :for="`rcs-test-number-${entryIndex}`">
              {{ t('storeParameters.services.rcsServiceParameter.wizard.testNumbers.fields.number') }}
            </label>
            <div class="rcs-test-number-input">
              <div class="rcs-test-number-input__country">
                <CountryDropdown
                  v-model="countryAndState"
                  :configuration="countryDropdownConfig"
                  :error="{ country: false, state: false }"
                />
              </div>
              <InputText
                :id="`rcs-test-number-${entryIndex}`"
                :model-value="getDisplayNumber(entry.number)"
                class="rcs-test-number-input__phone"
                inputmode="numeric"
                maxlength="20"
                :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.testNumbers.placeholders.number')"
                @update:model-value="updateDisplayNumber(entryIndex, String($event || ''))"
                @blur="normalizeEntry(entryIndex)"
              />
            </div>
            <small class="rcs-step__hint">
              {{ t('storeParameters.services.rcsServiceParameter.wizard.testNumbers.hints.number') }}
            </small>
          </div>
        </div>
      </div>
      <div class="rcs-carrier-repeater__actions">
        <button
          type="button"
          class="rcs-carrier-repeater__add"
          @click="addNumber"
        >
          <i class="fa-solid fa-plus" aria-hidden="true" />
          {{ t('storeParameters.services.rcsServiceParameter.wizard.testNumbers.actions.add') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { countriesPhonesCodes } from '@/configs/countries';
import { UserState } from '@/composables/User';
import CountryDropdown from '@/components/fields/CountryDropdown.vue';
import {
  CountryAndStateConfig,
  CountryAndStateValues,
} from '@/types/country-state-types';
import { normalizeRcsTestDevicePhoneNumber } from '@/types/rcs-onboarding';
import type { RcsDedicatedFormState } from '@/types/rcs-onboarding';

export default defineComponent({
  name: 'RcsTestNumbersStep',

  components: {
    CountryDropdown,
    InputText,
  },

  props: {
    form: {
      type: Object as PropType<RcsDedicatedFormState>,
      required: true,
    },
  },

  setup(props) {
    const { t } = useI18n();

    const testNumbers = computed(() => props.form.test_numbers);
    const countryAndState = ref<CountryAndStateValues>({
      country: (UserState.user.country ?? 'FR').toUpperCase() as CountryCode,
      state: '',
    });
    const countryDropdownConfig: CountryAndStateConfig = {
      showStates: false,
      showLabels: false,
      showFlags: true,
      showPhonesCodes: true,
      showOnlyPhoneCodeForSelection: true,
    };

    const normalizeEntry = (index: number): void => {
      const raw = testNumbers.value.numbers[index].number || '';
      const parsed = parsePhoneNumberFromString(
        raw,
        countryAndState.value.country as CountryCode,
      );
      if (parsed?.isValid()) {
        testNumbers.value.numbers[index].number = parsed.number;
        return;
      }
      const localDigits = raw.replace(/\D/g, '');
      if (!localDigits) {
        testNumbers.value.numbers[index].number = '';
        return;
      }
      const dialCode = countriesPhonesCodes[countryAndState.value.country?.toUpperCase()] || '';
      const dialDigits = dialCode.replace(/\D/g, '');
      testNumbers.value.numbers[index].number = dialDigits
        ? `+${dialDigits}${localDigits}`
        : normalizeRcsTestDevicePhoneNumber(raw);
    };

    const getDisplayNumber = (value: string): string => {
      const parsed = parsePhoneNumberFromString(value || '');
      if (parsed?.isValid()) {
        return parsed.nationalNumber;
      }
      return value || '';
    };

    const updateDisplayNumber = (index: number, value: string): void => {
      testNumbers.value.numbers[index].number = value;
    };

    const addNumber = (): void => {
      testNumbers.value.numbers.push({ number: '' });
    };

    const removeNumber = (index: number): void => {
      testNumbers.value.numbers.splice(index, 1);
    };

    return {
      t,
      testNumbers,
      countryAndState,
      countryDropdownConfig,
      getDisplayNumber,
      updateDisplayNumber,
      normalizeEntry,
      addNumber,
      removeNumber,
    };
  },
});
</script>

<style lang="scss" scoped>
.rcs-test-number-input {
  display: flex;
  gap: 0;
  align-items: center;
}

.rcs-test-number-input__country {
  flex: 0 0 10rem;
}

.rcs-test-number-input__phone {
  flex: 1 1 auto;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: -1px;
}

.rcs-test-number-input__country :deep(.p-dropdown) {
  height: 2.7rem;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.rcs-test-number-input__country :deep(.p-dropdown-label) {
  display: flex;
  align-items: center;
}

.rcs-test-number-input__phone {
  height: 2.7rem;
}

@media (max-width: 768px) {
  .rcs-test-number-input {
    flex-direction: column;
    align-items: stretch;
  }

  .rcs-test-number-input__country {
    flex: 1 1 auto;
  }
}
</style>
